import type { IRepository } from '@infra/persistence/IRepository';
import {
  BusinessApiClient,
  BusinessApiConflictError,
  BusinessApiError,
  getBusinessApiClient,
} from '@infra/api/BusinessApiClient';
import { RemoteSyncStore } from '@infra/sync/RemoteSyncStore';
import { logger } from '@infra/telemetry/Logger';

type VersionedEntity = { id: string; tenant_id: string };

function stripMeta(entity: VersionedEntity): Record<string, unknown> {
  const copy = { ...entity } as Record<string, unknown>;
  delete copy['id'];
  delete copy['tenant_id'];
  delete copy['_remoteVersion'];
  delete copy['_dirty'];
  return copy;
}

function mergeRow<T extends VersionedEntity>(row: {
  id: string;
  tenant_id: string;
  payload: Record<string, unknown>;
  version: number;
}): T {
  return {
    ...(row.payload as object),
    id: row.id,
    tenant_id: row.tenant_id,
    _remoteVersion: row.version,
  } as unknown as T;
}

/**
 * Échec passager d'une écriture en ligne (réseau, délai, 5xx, 429) : la modification part en file au lieu de faire
 * échouer la saisie. Un refus applicatif (licence, rôle, conflit de version) reste une erreur visible.
 */
function isTransientWriteFailure(err: unknown): boolean {
  if (err instanceof BusinessApiConflictError) return false;
  if (err instanceof BusinessApiError) {
    const status = err.statusCode;
    return status === undefined || status >= 500 || status === 429 || status === 408;
  }
  return true;
}

/**
 * Repository hybride : cache SQLite chiffré + API distante.
 * Hors ligne : lecture/écriture locale + file sync_outbox.
 */
export class CachedRemoteEntityRepository<T extends VersionedEntity> implements IRepository<T> {
  constructor(
    private readonly resource: string,
    private readonly tenantId: string,
    private readonly api: BusinessApiClient = getBusinessApiClient(),
  ) {}

  private async cacheEnabled(): Promise<boolean> {
    return RemoteSyncStore.isEnabled();
  }

  async findById(id: string): Promise<T | null> {
    const cacheEnabled = await this.cacheEnabled();
    const online = await this.api.confirmWriteReady();

    if (online) {
      try {
        const row = await this.api.getEntity(this.resource, id);
        if (!row) {
          return cacheEnabled
            ? ((await RemoteSyncStore.getCache<T>(this.tenantId, this.resource, id)) ?? null)
            : null;
        }
        if (cacheEnabled) {
          // Une modification locale non acquittée prime : la lecture ne l'écrase JAMAIS.
          const local = await RemoteSyncStore.getCache<T>(this.tenantId, this.resource, id);
          if (local?._dirty) return local;
          await RemoteSyncStore.putCache(
            this.tenantId,
            this.resource,
            row.id,
            row.payload,
            row.version,
            false,
          );
        }
        return mergeRow<T>(row);
      } catch (err) {
        logger.warn({
          event: 'remote_entity_read_fallback_cache',
          resource: this.resource,
          entityId: id,
          error: String(err),
        });
      }
    }

    if (cacheEnabled) {
      return (await RemoteSyncStore.getCache<T>(this.tenantId, this.resource, id)) ?? null;
    }
    return null;
  }

  async findAll(): Promise<T[]> {
    const cacheEnabled = await this.cacheEnabled();
    const online = await this.api.confirmWriteReady();

    if (online) {
      try {
        const rows = await this.api.listEntities(this.resource);
        if (cacheEnabled) {
          // Les fiches modifiées localement (ou créées hors ligne) et pas encore acquittées ne sont pas écrasées.
          const dirty = await RemoteSyncStore.listDirtyCache<T>(this.tenantId, this.resource);
          const dirtyIds = new Set(dirty.map((d) => d.id));
          for (const row of rows) {
            if (dirtyIds.has(row.id)) continue;
            await RemoteSyncStore.putCache(
              this.tenantId,
              this.resource,
              row.id,
              row.payload,
              row.version,
              false,
            );
          }
          return [...rows.filter((row) => !dirtyIds.has(row.id)).map((row) => mergeRow<T>(row)), ...dirty];
        }
        return rows.map((row) => mergeRow<T>(row));
      } catch (err) {
        logger.warn({
          event: 'remote_entity_list_fallback_cache',
          resource: this.resource,
          error: String(err),
        });
      }
    }

    if (cacheEnabled) {
      return RemoteSyncStore.listCache<T>(this.tenantId, this.resource);
    }
    return [];
  }

  async findBy(criteria: Partial<T>): Promise<T[]> {
    const all = await this.findAll();
    return all.filter((item) =>
      Object.entries(criteria).every(([k, v]) => (item as Record<string, unknown>)[k] === v),
    );
  }

  async save(entity: T): Promise<T> {
    const existing = await this.findById(entity.id);
    if (existing) {
      await this.update(entity);
      return entity;
    }
    await this.add(entity);
    return entity;
  }

  async saveMany(entities: T[]): Promise<T[]> {
    for (const e of entities) await this.save(e);
    return entities;
  }

  async add(entity: T): Promise<void> {
    const online = await this.api.confirmWriteReady();
    const body = stripMeta(entity);
    if (online && this.api.hasUserSession()) {
      try {
        const row = await this.api.createEntity(this.resource, body);
        if (await this.cacheEnabled()) {
          await RemoteSyncStore.putCache(
            this.tenantId,
            this.resource,
            row.id,
            row.payload,
            row.version,
            false,
          );
        }
        return;
      } catch (err) {
        // Échec passager : la saisie ne doit pas échouer, la création part en file.
        if (!isTransientWriteFailure(err) || !(await this.cacheEnabled())) throw err;
      }
    }
    if (!(await this.cacheEnabled())) {
      throw new Error('Hors ligne — cache SQLite indisponible');
    }
    await RemoteSyncStore.putCache(this.tenantId, this.resource, entity.id, body, 0, true);
    await RemoteSyncStore.enqueue({
      tenant_id: this.tenantId,
      resource: this.resource,
      action: 'create',
      entity_id: entity.id,
      payload: body,
      base_version: 0,
    });
    logger.info({ event: 'sync_outbox_enqueued', action: 'create', resource: this.resource });
  }

  async update(entity: T): Promise<void> {
    const version =
      ((entity as Record<string, unknown>)['_remoteVersion'] as number | undefined) ??
      (await RemoteSyncStore.getCache<T>(this.tenantId, this.resource, entity.id))
        ?._remoteVersion ??
      1;
    const body = stripMeta(entity);
    const online = await this.api.confirmWriteReady();

    if (online && this.api.hasUserSession()) {
      try {
        const row = await this.api.updateEntity(this.resource, entity.id, body, version);
        if (await this.cacheEnabled()) {
          await RemoteSyncStore.putCache(
            this.tenantId,
            this.resource,
            row.id,
            row.payload,
            row.version,
            false,
          );
        }
        return;
      } catch (err) {
        if (err instanceof BusinessApiConflictError) {
          const remote = await this.api.getEntity(this.resource, entity.id);
          if (remote) {
            await RemoteSyncStore.saveConflict({
              tenant_id: this.tenantId,
              resource: this.resource,
              entity_id: entity.id,
              local_payload: body,
              local_version: version,
              remote_payload: remote.payload,
              remote_version: remote.version,
            });
          }
        }
        // Échec passager : la modification part en file au lieu de faire échouer la saisie.
        if (!isTransientWriteFailure(err) || !(await this.cacheEnabled())) throw err;
      }
    }

    if (!(await this.cacheEnabled())) {
      throw new Error('Hors ligne — cache SQLite indisponible');
    }
    await RemoteSyncStore.putCache(this.tenantId, this.resource, entity.id, body, version, true);
    await RemoteSyncStore.enqueue({
      tenant_id: this.tenantId,
      resource: this.resource,
      action: 'update',
      entity_id: entity.id,
      payload: body,
      base_version: version,
    });
    logger.info({ event: 'sync_outbox_enqueued', action: 'update', resource: this.resource });
  }

  async delete(id: string): Promise<void> {
    const online = await this.api.confirmWriteReady();
    if (online && this.api.hasUserSession()) {
      try {
        await this.api.deleteEntity(this.resource, id);
        if (await this.cacheEnabled()) {
          await RemoteSyncStore.removeCache(this.tenantId, this.resource, id);
        }
        return;
      } catch (err) {
        if (!isTransientWriteFailure(err) || !(await this.cacheEnabled())) throw err;
      }
    }
    if (!(await this.cacheEnabled())) {
      throw new Error('Hors ligne — cache SQLite indisponible');
    }
    await RemoteSyncStore.removeCache(this.tenantId, this.resource, id);
    await RemoteSyncStore.enqueue({
      tenant_id: this.tenantId,
      resource: this.resource,
      action: 'delete',
      entity_id: id,
      payload: {},
      base_version: 0,
    });
  }

  async count(criteria?: Partial<T>): Promise<number> {
    const items = criteria ? await this.findBy(criteria) : await this.findAll();
    return items.length;
  }

  async get(id: string): Promise<T | undefined> {
    return (await this.findById(id)) ?? undefined;
  }

  async list(): Promise<T[]> {
    return this.findAll();
  }
}
